import { memo, useCallback, useId, useState } from "react";
import {
	BaseListContainer,
	BaseListFooterContainer,
	BaseListHeaderContainer,
	BaseListInfoArea,
	CommonListProps,
	EditQuizNoResults,
} from "./OptionListsComponents";
import { CoreList, CoreListGroup, CoreListRow, OverflowText } from "../../../core";
import i18nPackage from "../../../i18n/i18n";
import sharedStyles from "./OptionListsComponents.module.css";
import { Extension } from "@verity/proctoring-option/types";
import AddExtensionNameModal from "../option-modals/AddExtensionModal";
import ExtensionUnknown from "../../images/configuration/extension-unknown.svg?react";
import { Typography } from "@mui/material";
import styles from "./DisableExtensionList.module.css";
import { useFocusElementOnMount } from "../../../hooks";
import useGroupedList from "../hooks/useGroupedList";
import { Trans } from "react-i18next";
import { GroupedListSelectAllRow, GroupedListToolbar } from "./GroupedList";
import { PROCTORING_OPTION_TEXT } from "../option-text";

function ExtensionLabel({ extension }: { extension: Extension }) {
	return (
		<>
			<OverflowText component={"span"} className={styles.extensionName}>
				{extension.name}
			</OverflowText>
			<Typography component={"span"} className={styles.extensionId}>
				{extension.id}
			</Typography>
		</>
	);
}

// Memoed because the set inner html was causing lag on chrome extension
const MemoizedExtensionRow = memo(function MemoizedExtensionRow({
	extension,
	checked,
	onSelect,
}: {
	extension: Extension;
	checked: boolean;
	onSelect: (selected: boolean) => void;
}) {
	return (
		<CoreListRow
			key={extension.id}
			label={<ExtensionLabel extension={extension} />}
			checkboxAriaLabel={extension.name}
			icon={<ExtensionIcon extension={extension} />}
			checked={checked}
			onSelect={onSelect}
		/>
	);
});

const ExtensionIcon = memo(function ExtensionIcon({ extension }: { extension: Extension }) {
	if (extension.type !== "Other" && extension.iconSvg) {
		return <div className={styles.extensionIcon} dangerouslySetInnerHTML={{ __html: extension.iconSvg }} />;
	}
	return <ExtensionUnknown className={styles.extensionIcon} />;
});

type ExtensionCategory = Extension["type"];

type DisableExtensionListProps = {
	allExtensions: Extension[];
	initialSelectedExtensionNames: string[];
	fetchChromeExtensionData: (id: string) => Promise<Extension | null>;
	onUpdate: (processNames: string[]) => void;
} & CommonListProps;

export default function DisableExtensionList({
	allExtensions,
	initialSelectedExtensionNames,
	fetchChromeExtensionData,
	onUpdate,
	focusOnMount,
	onFocus,
}: DisableExtensionListProps) {
	const [showAddModal, setShowAddModal] = useState(false);
	const { ref } = useFocusElementOnMount<HTMLDivElement>(focusOnMount, onFocus);
	const controlId = useId();

	const { actions, filter, items, state } = useGroupedList<Extension, ExtensionCategory>({
		categoryExtractor: (ext) => ext.type,
		formatForParent: (ext) => ext.name + ext.id,
		initialItems: allExtensions,
		initialSelected: initialSelectedExtensionNames,
		isInitialSelected: (ext, rawStrings) => rawStrings.some((raw) => raw.endsWith(ext.id)),
		keyExtractor: (ext) => ext.id,
		onUpdate,
		searchSelector: (ext) => ext.name,
		sortFunction: (a, b) => a.name.localeCompare(b.name),
	});

	const { activeStatus, searchText, setActiveStatus, setSearchText } = filter;
	const { displayedItems, groupedItems } = items;

	const handleAddCustomExtension = useCallback(
		(newExtension: Extension) => {
			actions.handleAddNewItem(newExtension);
			actions.handleCategoryExpand("Other", true);
		},
		[actions],
	);

	const ListIcon = PROCTORING_OPTION_TEXT["extensionBlockMode"]["icon"];

	return (
		<BaseListContainer ref={ref}>
			<BaseListHeaderContainer>
				<BaseListInfoArea
					title={i18nPackage.t("editQuizPage.disableExtensionList.title")}
					description={
						<Typography className={sharedStyles.baseListDescription}>
							<Trans
								i18n={i18nPackage}
								i18nKey={"editQuizPage.disableExtensionList.description"}
								components={{
									b: <strong />,
								}}
							/>
						</Typography>
					}
					icon={ListIcon ? <ListIcon /> : undefined}
				/>
				<GroupedListToolbar
					slotProps={{
						filter: {
							activeStatus,
							ariaLabel: i18nPackage.t("editQuizPage.disableExtensionList.selectAriaLabel"),
							renderFilterLabel: (value) => i18nPackage.t(`editQuizPage.disableExtensionList.filter.${value}`),
							setActiveStatus,
						},
						textfield: {
							controlId,
							placeholder: i18nPackage.t("editQuizPage.disableExtensionList.placeholder"),
							searchText,
							setSearchText,
							totalResults: displayedItems.length,
						},
					}}
				/>
			</BaseListHeaderContainer>
			{groupedItems.length > 0 && (
				<GroupedListSelectAllRow
					checked={state.allSelected}
					onChange={actions.handleSelectAll}
					onClick={() => setShowAddModal(true)}
					badgeText={
						state.numSelected === 0
							? undefined
							: i18nPackage.t("editQuizPage.disableExtensionList.badgeText", { count: state.numSelected })
					}
					buttonText={i18nPackage.t("editQuizPage.disableExtensionList.addCustomExtension")}
				/>
			)}
			<BaseListFooterContainer>
				{groupedItems.length === 0 ? (
					<EditQuizNoResults />
				) : (
					<CoreList id={controlId} maxHeight={302}>
						{groupedItems.map((group) => {
							const category = group.category;
							const numSelected = state.getNumItemsSelectedInCategory(group);
							return (
								<CoreListGroup
									key={category}
									title={category}
									isSelected={state.isAllItemsInCategorySelected(group)}
									onSelect={(selected) => actions.handleSelectAllInCategory(group.items, selected)}
									expanded={state.isCategoryExapnded(category)}
									onExpand={() => actions.handleCategoryExpand(category)}
									isIndeterminate={state.isCategoryIndeterminate(group)}
									slotProps={{
										...(numSelected !== 0 && {
											chip: {
												text: i18nPackage.t("editQuizPage.disableExtensionList.badgeText", { count: numSelected }),
											},
										}),
									}}
								>
									{group.items.map((extension) => (
										<MemoizedExtensionRow
											key={extension.id}
											extension={extension}
											checked={state.isItemSelected(extension)}
											onSelect={(selected) => actions.handleToggleItem(extension, selected)}
										/>
									))}
								</CoreListGroup>
							);
						})}
					</CoreList>
				)}
			</BaseListFooterContainer>
			{showAddModal && (
				<AddExtensionNameModal
					setShowAddModal={setShowAddModal}
					onAddExtension={handleAddCustomExtension}
					fetchChromeExtensionData={fetchChromeExtensionData}
				/>
			)}
		</BaseListContainer>
	);
}
